import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, AuthUser } from '@/api/auth';
import { usersApi } from '@/api/client';

interface AuthContextType {
  user: any;
  authUser: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signInWithGoogle: () => void;
  logout: () => void;
  setAuthFromCallback: (authUser: AuthUser) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<any>(null);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const session = await authService.getSession();
      if (session) {
        const cognitoUser = await authService.getUserFromIdToken();
        setAuthUser(cognitoUser);
        try {
          const response = await usersApi.getMe();
          setUser(response.data);
        } catch {
          // API user fetch may fail on first load before backend creates user
        }
      } else {
        setAuthUser(null);
        setUser(null);
      }
    } catch {
      setAuthUser(null);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await refreshUser();
      setIsLoading(false);
    };
    init();
  }, [refreshUser]);

  const signInWithGoogle = () => authService.signInWithGoogle();

  const logout = () => {
    authService.signOut();
    setUser(null);
    setAuthUser(null);
  };

  const setAuthFromCallback = useCallback(async (callbackAuthUser: AuthUser) => {
    setAuthUser(callbackAuthUser);
    try {
      const response = await usersApi.getMe();
      setUser(response.data);
    } catch {
      // User creation happens on first API call via auth middleware
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      authUser,
      isAuthenticated: !!authUser,
      isLoading,
      signInWithGoogle,
      logout,
      setAuthFromCallback,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
